/**
 * Value formatting functions for campaign and campaign action fields.
 *
 * These functions format raw backend values to human-readable display labels
 * that match the Customer.io UI terminology.
 */

import { isEmptyString } from '@/utils/string';
import { tryDecodeBase64 } from '../formatting';
import { ActionTypeLabels } from './action-types';
import { FIELD_VALUE_MAPPINGS } from './field-mappings';
import { NewsletterChannelLabels } from '../newsletter-mapping';

/**
 * Format an event name object (used in conversion events and exit conditions).
 * The backend stores these as { name: "base64_encoded_name", etype: "event_type" }
 *
 * @param eventObj - The event object with name and etype properties
 * @returns Human-readable event name
 */
export function formatEventNameObject(eventObject: unknown): string {
  if (eventObject == null) {
    return '—';
  }

  if (typeof eventObject === 'object' && !Array.isArray(eventObject)) {
    const object = eventObject as Record<string, unknown>;
    const name = object.name as string | undefined;
    const etype = object.etype as string | undefined;

    if (name != null) {
      // The name is often base64 encoded, try to decode it
      const decodedName = tryDecodeBase64(name);

      // If we have both name and etype, format nicely
      if (!isEmptyString(etype)) {
        return `${decodedName} (${etype})`;
      }
      return decodedName;
    }
  }

  // If it's a string, return as-is (try decoding if base64)
  if (typeof eventObject === 'string') {
    return tryDecodeBase64(eventObject);
  }

  return JSON.stringify(eventObject);
}

/**
 * Check if a value is an event name object (has name and optionally etype).
 */
function isEventNameObject(value: unknown): boolean {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const object = value as Record<string, unknown>;
  return 'name' in object && (typeof object.name === 'string' || object.name == null);
}

/**
 * Maps normalized field names (lowercase, no underscores/hyphens) to their semantic labels.
 * Used by both formatBooleanFieldValue and isCampaignBooleanField.
 * Labels match UI terminology from ~/code/ui VWB components.
 */
const BOOLEAN_FIELD_LABELS: Record<string, { true: string; false: string }> = {
  // Exit condition booleans (from exit-settings component)
  exitontriggerorfilternotmatched: {
    true: 'They stop matching the trigger or filters',
    false: "Don't exit when they stop matching",
  },
  exitonconversionmatched: {
    true: 'They match the conversion criteria',
    false: "Don't exit on conversion criteria match",
  },
  exitonlyaftermessage: {
    true: 'Only exit after a message is sent',
    false: 'Can exit immediately',
  },
  // Subscription settings (from audience component)
  sendtounsubscribed: {
    true: 'Send to unsubscribed people',
    false: "Don't send to unsubscribed",
  },
  sendtounsubs: {
    true: 'Send to unsubscribed people',
    false: "Don't send to unsubscribed",
  },
  useglobalsendtounsubs: {
    true: 'Use campaign settings',
    false: 'Override campaign settings',
  },
  // Message limits (from message-limits component)
  usemessagelimits: {
    true: 'Count toward limit',
    false: "Don't count toward limit",
  },
  useglobalmessagelimits: {
    true: 'Use campaign settings',
    false: 'Override campaign settings',
  },
  useworkspacemessagelimitsretry: {
    true: 'Use campaign settings',
    false: 'Use custom retry settings',
  },
  overrideglobalmessagelimitsretry: {
    true: 'Use custom retry window',
    false: 'Use campaign settings',
  },
  // Tracking
  tracked: {
    true: 'Link tracking enabled',
    false: 'Link tracking disabled',
  },
  // Conversion criteria
  conversion: {
    true: 'Has conversion criteria',
    false: 'No conversion criteria',
  },
  hasconversion: {
    true: 'Has conversion criteria',
    false: 'No conversion criteria',
  },
  // Action-specific booleans
  holdout: {
    true: 'Holdout test enabled',
    false: 'Holdout test disabled',
  },
  awaitcompletion: {
    true: 'Wait for response',
    false: 'Send and continue',
  },
  canconvert: {
    true: 'Can trigger conversion',
    false: 'Cannot trigger conversion',
  },
  // Time settings
  usecustomerzone: {
    true: "Use customer's timezone",
    false: 'Use campaign timezone',
  },
  // Collection query booleans
  asc: {
    true: 'Ascending order',
    false: 'Descending order',
  },
  randomize: {
    true: 'Randomize results',
    false: 'Ordered results',
  },
  singleresult: {
    true: 'Return single result',
    false: 'Return multiple results',
  },
  // General booleans
  enabled: {
    true: 'Enabled',
    false: 'Disabled',
  },
};

/**
 * Normalize a field name for lookup in BOOLEAN_FIELD_LABELS.
 * Removes underscores, hyphens, and converts to lowercase.
 *
 * WHY NORMALIZATION IS NEEDED:
 * Field names can arrive in different formats depending on their source:
 * - Backend API returns snake_case: "exit_on_trigger_or_filter_not_matched"
 * - Frontend code may use camelCase: "exitOnTriggerOrFilterNotMatched"
 * - Some fields use hyphens: "exit-on-trigger"
 *
 * By normalizing to lowercase without separators, we can match all formats
 * against a single canonical key in BOOLEAN_FIELD_LABELS.
 *
 * Example: All of these normalize to "exitontriggerorfilternotmatched":
 * - "exit_on_trigger_or_filter_not_matched" (snake_case)
 * - "exitOnTriggerOrFilterNotMatched" (camelCase - after toLowerCase)
 * - "Exit-On-Trigger-Or-Filter-Not-Matched" (kebab-case)
 */
function normalizeFieldName(fieldName: string): string {
  return fieldName.toLowerCase().replace(/[_-]/g, '');
}

/**
 * Format boolean field values with campaign-specific semantic labels.
 */
function formatBooleanFieldValue(fieldName: string, value: boolean): string {
  const normalizedName = normalizeFieldName(fieldName);
  const labels = BOOLEAN_FIELD_LABELS[normalizedName];

  if (labels != null) {
    return value ? labels.true : labels.false;
  }

  // Default boolean formatting
  return value ? 'Yes' : 'No';
}

// Fields that contain event name objects (conversions, exit conditions)
const EVENT_NAME_FIELDS = new Set([
  'event_name',
  'eventname',
  'conversion_event',
  'conversionevent',
  'exit_event',
  'exitevent',
  'conversion_event_name',
  'conversioneventname',
]);

/**
 * Format a campaign or campaign action field value to a human-readable label.
 *
 * @param fieldName - The name of the field (e.g., 'type', 'state', 'restart_mode')
 * @param value - The raw value from the audit log
 * @param objectType - Optional object type to provide context (e.g., 'Campaign', 'CampaignAction')
 * @returns The human-readable label, or the original value if no mapping exists
 *
 * @example
 * formatCampaignValue('type', 'seg_attr') // 'Attribute triggered'
 * formatCampaignValue('type', 15, 'CampaignAction') // 'Exit'
 * formatCampaignValue('state', 'running') // 'Running'
 * formatCampaignValue('restart_mode', 'none') // 'Never re-enter'
 * formatCampaignValue('event_name', { name: 'bGFuZGluZw==', etype: '' }) // 'landing'
 */
export function formatCampaignValue(fieldName: string, value: unknown, objectType?: string): string {
  if (value == null) {
    return '—';
  }

  const lowerFieldName = fieldName.toLowerCase();

  // Special handling for 'type' field based on object context
  // For CampaignAction, 'type' refers to ActionType enum (numeric values 0-28)
  // For Campaign, 'type' refers to CampaignType enum (string or numeric values)
  if (lowerFieldName === 'type' && objectType === 'CampaignAction') {
    const stringValue = String(value);
    if (ActionTypeLabels[stringValue] != null) {
      return ActionTypeLabels[stringValue];
    }
    // Try numeric value directly
    if (typeof value === 'number' && ActionTypeLabels[value] != null) {
      return ActionTypeLabels[value];
    }
  }

  // Special handling for Newsletter 'type' field (channel)
  if (lowerFieldName === 'type' && objectType === 'Newsletter') {
    const stringValue = String(value);
    if (NewsletterChannelLabels[stringValue] != null) {
      return NewsletterChannelLabels[stringValue];
    }
  }

  // Special handling for event_name objects (used in conversions/exit conditions)
  if (
    (lowerFieldName === 'event_name' ||
      lowerFieldName === 'eventname' ||
      lowerFieldName === 'conversion_event' ||
      lowerFieldName === 'conversionevent' ||
      lowerFieldName === 'exit_event' ||
      lowerFieldName === 'exitevent') &&
    isEventNameObject(value)
  ) {
    return formatEventNameObject(value);
  }

  // Check if value is an event name object regardless of field name
  if (isEventNameObject(value)) {
    const object = value as Record<string, unknown>;
    // Only format as event if it has the expected structure
    if ('etype' in object || (typeof object.name === 'string' && /^[\d+/=A-Za-z]+$/.test(object.name))) {
      return formatEventNameObject(value);
    }
  }

  const stringValue = String(value);

  // Check exact field name match first
  if (FIELD_VALUE_MAPPINGS[fieldName] != null) {
    const mapping = FIELD_VALUE_MAPPINGS[fieldName];
    if (mapping[stringValue] != null) {
      return mapping[stringValue];
    }
    // Try numeric value directly for numeric mappings
    if (typeof value === 'number' && mapping[value] != null) {
      return mapping[value];
    }
  }

  // Check lowercase field name match
  if (FIELD_VALUE_MAPPINGS[lowerFieldName] != null) {
    const mapping = FIELD_VALUE_MAPPINGS[lowerFieldName];
    if (mapping[stringValue] != null) {
      return mapping[stringValue];
    }
    // Try numeric value directly for numeric mappings
    if (typeof value === 'number' && mapping[value] != null) {
      return mapping[value];
    }
  }

  // Special handling for boolean fields with semantic meaning
  if (typeof value === 'boolean') {
    return formatBooleanFieldValue(fieldName, value);
  }

  // Return original value if no mapping found
  return stringValue;
}

/**
 * Check if a field name is a known campaign/action field that has semantic value mapping.
 */
export function isCampaignMappedField(fieldName: string): boolean {
  const lowerFieldName = fieldName.toLowerCase();
  return (
    FIELD_VALUE_MAPPINGS[fieldName] != null ||
    FIELD_VALUE_MAPPINGS[lowerFieldName] != null ||
    isCampaignBooleanField(fieldName) ||
    EVENT_NAME_FIELDS.has(lowerFieldName)
  );
}

/**
 * Check if a field name is a campaign boolean field with semantic meaning.
 */
export function isCampaignBooleanField(fieldName: string): boolean {
  const normalizedName = normalizeFieldName(fieldName);
  return BOOLEAN_FIELD_LABELS[normalizedName] != null;
}

/**
 * Fields that contain duration values in seconds.
 * These should be formatted as human-readable durations (e.g., "8 days" instead of "691200").
 *
 * Used primarily in CampaignAction for time-based settings.
 */
const DURATION_SECONDS_FIELDS = new Set([
  'delay',
  'debounce_secs',
  'debouncesecs',
  'message_limits_retry_window_secs',
  'messagelimitsretrywindowsecs',
  'restart_min_interval',
  'restartmininterval',
  'test_duration', // A/B test duration
  'testduration',
  'conversion_window', // Newsletter conversion window
  'conversionwindow',
  'rate_limit_time_period', // Newsletter rate limit period
  'ratelimittimeperiod',
  'within', // Condition "within" duration
  'grace_period', // Grace period duration
  'graceperiod',
  'delay_event_attr_offset', // Wait Until offset
  'delayeventattroffset',
  'delay_event_attribute_offset',
  'delayeventattributeoffset',
  'minimum_delay', // Randomized delay min
  'minimumdelay',
  'maximum_delay', // Randomized delay max
  'maximumdelay',
]);

/**
 * Check if a field contains duration values in seconds that should be formatted.
 */
export function isDurationSecondsField(fieldName: string): boolean {
  const normalizedName = normalizeFieldName(fieldName);
  return DURATION_SECONDS_FIELDS.has(normalizedName) || DURATION_SECONDS_FIELDS.has(fieldName.toLowerCase());
}
